/* AL AHMED video analytics v1. Consent-gated, anonymous browser events. */
(() => {
  'use strict';
  if (window.AAVideoAnalyticsV1 || window.Shopify?.designMode) return;
  window.AAVideoAnalyticsV1 = true;
  const nativeFetch = window.fetch.bind(window);
  const root = window.Shopify?.routes?.root || '/';
  const endpoint = root + 'apps/al-ahmed-reviews?resource=video-analytics';
  const KEY = 'aa_video_v1', SESSION = 'aa_video_session_v1';
  const DAY = 86400000;
  let privacyReady = false, state = null, session = null, cart = '', lastStamp = '', stampQueue = Promise.resolve();
  const sending = new Set();
  const cards = new WeakSet(), visibility = new WeakMap(), media = new WeakMap();
  const id = () => crypto.randomUUID();
  const allowed = () => privacyReady && window.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true;
  const read = (store,key) => { try { return JSON.parse(store.getItem(key)); } catch { return null; } };
  const write = (store,key,value) => { try { store.setItem(key,JSON.stringify(value)); } catch {} };
  function initState() {
    if (!allowed()) return false;
    try {
      if (!state) {
        state = read(localStorage,KEY);
        if (!state || typeof state.visitor !== 'string' || !Array.isArray(state.touches) || Date.now()-state.created > 30*DAY)
          state = {visitor:id(),created:Date.now(),touches:[]};
        state.touches = state.touches.filter(t => t.at > Date.now()-7*DAY).slice(-30);
      }
      session = read(sessionStorage,SESSION);
      if (!session || Date.now()-session.at > 30*60000) session = {id:id(),at:Date.now(),events:{},pending:{}};
      session.pending ||= {};
      session.at=Date.now();
      write(localStorage,KEY,state); write(sessionStorage,SESSION,session);
      return true;
    } catch { return false; }
  }
  async function eventId(key) {
    const bytes = new Uint8Array(await crypto.subtle.digest('SHA-256',new TextEncoder().encode(session.id+'|'+key)));
    bytes[6]=(bytes[6]&15)|64;bytes[8]=(bytes[8]&63)|128;
    const hex=[...bytes.slice(0,16)].map(x=>x.toString(16).padStart(2,'0')).join('');
    return [hex.slice(0,8),hex.slice(8,12),hex.slice(12,16),hex.slice(16,20),hex.slice(20)].join('-');
  }
  async function getCart() {
    const r=await nativeFetch(root+'cart.js',{credentials:'same-origin',signal:AbortSignal.timeout(5000)});
    if(!r.ok)throw Error('Cart unavailable');
    const data=await r.json();cart=typeof data.token==='string'?data.token.split('?')[0]:'';return data;
  }
  function saveTouch(info,kind,token) {
    const touch={...info,kind,token,at:state.touches.find(t=>t.token===token)?.at||Date.now()};
    state.touches=state.touches.filter(t=>!(t.videoId===info.videoId&&t.kind===kind));
    state.touches.push(touch);state.touches=state.touches.slice(-30);write(localStorage,KEY,state);
    updateForms();
    // Do not intercept or delay customer checkout navigation.
    stampQueue=stampQueue.catch(()=>{}).then(async()=>{
      if(!allowed()||lastStamp===token)return;
      const r=await nativeFetch(root+'cart/update.js',{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json'},body:JSON.stringify({attributes:{__aa_video_touch:token}}),signal:AbortSignal.timeout(5000)});
      if(r.ok)lastStamp=token;
    });
  }
  async function record(info,kind,refresh=false) {
    if(!initState()||!info.videoId)return;
    const key=kind+':'+info.videoId;
    if(sending.has(key)||(session.events[key]===true&&!refresh))return;
    session.pending[key]={info,kind};write(sessionStorage,SESSION,session);sending.add(key);
    try{
      if(!allowed())return;
      const r=await nativeFetch(endpoint,{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json'},body:JSON.stringify({id:await eventId(key),visitor:state.visitor,session:session.id,videoId:info.videoId,title:info.title.slice(0,160),productId:info.productId||'',kind,consent:true,...(cart?{cart}:{})}),keepalive:true,signal:AbortSignal.timeout(8000)});
      if(!r.ok)throw Error('Tracking unavailable');
      const data=await r.json();
      session.events[key]=true;delete session.pending[key];write(sessionStorage,SESSION,session);
      if(allowed()&&data.touch)saveTouch(info,kind,data.touch);
    }catch{ /* Pending analytics may resume on the next page; purchases are never retried. */ }
    finally{sending.delete(key);}
  }
  const infoOf = card => ({videoId:card.dataset.aaVideoId,title:card.dataset.aaVideoTitle||'Product video',productId:card.dataset.aaProductId||''});
  function bestTouch(productId) {
    if(!state)return null;
    const list=state.touches.filter(t=>t.at>Date.now()-7*DAY&&(!productId||t.productId===String(productId)));
    return list.filter(t=>t.kind==='CLICK').sort((a,b)=>b.at-a.at)[0] || list.sort((a,b)=>b.at-a.at)[0];
  }
  function updateForms() {
    if(!allowed())return;
    document.querySelectorAll('form[action*="/cart/add"]').forEach(form=>{
      const card=form.closest('[data-aa-video-id]');
      const pid=card?.dataset.aaProductId || form.querySelector('[name="product-id"]')?.value;
      if(!pid)return;
      const t=bestTouch(pid);if(!t)return;
      let input=form.querySelector('[name="properties[_aa_video_touch]"]');
      if(!input){input=document.createElement('input');input.type='hidden';input.name='properties[_aa_video_touch]';form.appendChild(input);}
      input.value=t.token;
    });
  }
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries=>{
    entries.forEach(e=>visibility.set(e.target,e.intersectionRatio>=.5));
  },{threshold:[0,.5,1]}) : null;
  function bind() {
    document.querySelectorAll('[data-aa-video-id]').forEach(card=>{
      if(cards.has(card))return;cards.add(card);
      const info=infoOf(card),video=card.querySelector('video');
      if(video){
        observer?.observe(video);media.set(video,{elapsed:0,last:0});
        video.addEventListener('timeupdate',()=>{
          const s=media.get(video),now=performance.now();
          if(allowed()&&!document.hidden&&visibility.get(video)&&!video.paused&&!video.seeking&&video.readyState>=3){
            if(s.last)s.elapsed+=Math.min((now-s.last)/1000,.6);
            s.last=now;
            if(s.elapsed>=3)void record(info,'VIEW');
            if(s.elapsed>=3&&Number.isFinite(video.duration)&&video.duration>0&&s.elapsed>=video.duration*.9)void record(info,'COMPLETE');
          }else s.last=0;
        });
        for(const name of ['pause','seeking','waiting'])video.addEventListener(name,()=>{media.get(video).last=0;});
      }
      card.addEventListener('click',e=>{
        const a=e.target.closest('a');
        if(a){try{const u=new URL(a.href,location.href);if(u.origin===location.origin&&u.pathname.includes('/products/'))void record(info,'CLICK');}catch{}}
        if(e.target.closest('[data-load-instagram],.aa-videos__instagram-link'))void record(info,'IG_OPEN');
      });
      card.querySelectorAll('form[action*="/cart/add"]').forEach(form=>form.addEventListener('submit',()=>{void record(info,'CLICK');updateForms();}));
    });
    updateForms();
  }
  async function afterAdd(body) {
    if(!initState())return;
    const items=body?.items || (body?.product_id?[body]:[]);
    for(const item of items){const t=bestTouch(item.product_id);if(t)void record(t,'CART');}
    try{
      await getCart();
      const t=bestTouch();if(t)await record(t,t.kind,true);
    }catch{}
  }
  // Observe successful Ajax adds without changing request arguments, response, cart drawer or checkout.
  window.fetch=function(...args){
    const result=nativeFetch(...args);
    try{
      const u=new URL(typeof args[0]==='string'?args[0]:args[0] instanceof Request?args[0].url:String(args[0]),location.href);
      const method=args[1]?.method || (args[0] instanceof Request?args[0].method:'GET');
      if(u.origin===location.origin&&/\/cart\/add(?:\.js)?$/.test(u.pathname)&&method.toUpperCase()==='POST')
        void result.then(r=>{if(r.ok&&r.headers.get('content-type')?.includes('application/json'))return r.clone().json().then(afterAdd);}).catch(()=>{});
    }catch{}
    return result;
  };
  async function start() {
    if(!initState())return;
    bind();
    for(const item of Object.values(session.pending))void record(item.info,item.kind,true);
    try{
      const data=await getCart();
      const pending=read(sessionStorage,'aa_video_pending_add_v1');
      for(const item of data.items||[]){
        const token=item.properties?._aa_video_touch;
        if(!pending||pending.token!==token||Date.now()-pending.at>60000)continue;
        const t=state.touches.find(t=>t.token===token&&t.at>Date.now()-7*DAY&&t.productId===String(item.product_id));
        if(t){void record(t,'CART');sessionStorage.removeItem('aa_video_pending_add_v1');}
      }
    }catch{}
  }
  document.addEventListener('visitorConsentCollected',()=>{
    if(allowed()){void start();return;}
    state=null;session=null;lastStamp='';
    try{localStorage.removeItem(KEY);sessionStorage.removeItem(SESSION);sessionStorage.removeItem('aa_video_pending_add_v1');}catch{}
    document.querySelectorAll('[name="properties[_aa_video_touch]"]').forEach(el=>el.remove());
    // Remove only our cart attribute on revocation. No analytics request is sent.
    stampQueue=stampQueue.catch(()=>{}).then(()=>nativeFetch(root+'cart/update.js',{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json'},body:JSON.stringify({attributes:{__aa_video_touch:''}}),signal:AbortSignal.timeout(5000)})).catch(()=>{});
  });
  document.addEventListener('shopify:section:load',bind);
  document.addEventListener('submit',e=>{
    if(!allowed()||!e.target.matches('form[action*="/cart/add"]'))return;
    updateForms();
    const token=e.target.querySelector('[name="properties[_aa_video_touch]"]')?.value;
    if(token)write(sessionStorage,'aa_video_pending_add_v1',{token,at:Date.now()});
  },true);
  function ready(){privacyReady=!!window.Shopify?.customerPrivacy;void start();}
  if(window.Shopify?.customerPrivacy)ready();
  else if(window.Shopify?.loadFeatures)window.Shopify.loadFeatures([{name:'consent-tracking-api',version:'0.1'}],error=>{if(!error)ready();});
})();
